import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { Observable } from 'rxjs';
import { EstablishmentService } from '../../services/establishment/establishment.service';
import { ToastService } from '../../services/toast/toast.service';
import { LoaderComponent } from '../../atoms/loader/loader.component';
import { CsvColumn, downloadCsv } from '../../common-files/csv';

type ReportKey =
  | 'occupancy' | 'summary' | 'timeline' | 'utilization' | 'movements' | 'noShows'
  | 'demandSupply' | 'cancellations' | 'velocity' | 'guests' | 'frontdesk';

// branch reports for one utsav: pick the utsav, then a report tab. Each tab is
// one API call; the data is kept per tab so switching back does not refetch.
@Component({
  selector: 'app-establishment-reports',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatSelectModule,
    LoaderComponent,
  ],
  templateUrl: './establishment-reports.component.html',
  styleUrls: ['./establishment-reports.component.scss', '../../../common-style/report-page.scss'],
})
export class EstablishmentReportsComponent implements OnInit {
  utsav = new FormControl<number | null>(null);

  readonly reportTabs: Array<{ key: ReportKey; label: string }> = [
    { key: 'occupancy', label: 'Occupancy' },
    { key: 'summary', label: 'Booking summary' },
    { key: 'timeline', label: 'Occupancy timeline' },
    { key: 'utilization', label: 'Utilization' },
    { key: 'movements', label: 'Arrivals & departures' },
    { key: 'noShows', label: 'No-shows' },
    { key: 'demandSupply', label: 'Demand vs supply' },
    { key: 'cancellations', label: 'Cancellations' },
    { key: 'velocity', label: 'Booking velocity' },
    { key: 'guests', label: 'Guest composition' },
    { key: 'frontdesk', label: 'Front-desk activity' },
  ];
  activeReport: ReportKey = 'occupancy';

  utsavs: any[] = [];
  report: any = null;
  expandedPropertyId: number | null = null;
  hasSelected: boolean = false;
  hasLoadedUtsavs: boolean = false;
  utsavsLoadFailed: boolean = false;
  reportLoadFailed: boolean = false;
  isLoading: boolean = false;

  constructor(
    private establishmentService: EstablishmentService,
    private toastService: ToastService
  ) {}

  ngOnInit(): void {
    this.loadUtsavs();
  }

  loadUtsavs(): void {
    this.isLoading = true;
    this.establishmentService.listUtsavs().subscribe({
      next: (res: any) => {
        this.utsavs = res?.data?.utsavs || [];
        this.utsavsLoadFailed = false;
        this.hasLoadedUtsavs = true;
        this.isLoading = false;
      },
      error: (err) => {
        this.utsavs = [];
        this.utsavsLoadFailed = true;
        this.hasLoadedUtsavs = true;
        this.isLoading = false;
        this.toastService.error(err?.error?.message || 'Failed to load utsavs.');
      },
    });
  }

  setReport(key: ReportKey): void {
    if (this.activeReport === key) return;
    this.activeReport = key;
    this.expandedPropertyId = null;
    this.loadReport();
  }

  loadReport(): void {
    const utsavId = this.utsav.value;
    if (!utsavId) {
      this.report = null;
      this.hasSelected = false;
      this.reportLoadFailed = false;
      return;
    }

    this.report = null;
    this.expandedPropertyId = null;
    this.hasSelected = true;
    this.reportLoadFailed = false;
    this.isLoading = true;
    this.requestFor(this.activeReport, utsavId).subscribe({
      next: (res: any) => {
        this.report = res?.data || null;
        this.reportLoadFailed = false;
        this.isLoading = false;
      },
      error: (err) => {
        this.report = null;
        this.reportLoadFailed = true;
        this.isLoading = false;
        this.toastService.error(err?.error?.message || 'Failed to load report.');
      },
    });
  }

  private requestFor(key: ReportKey, utsavId: number): Observable<any> {
    switch (key) {
      case 'summary':
        return this.establishmentService.getBookingSummaryReport(utsavId);
      case 'timeline':
        return this.establishmentService.getOccupancyTimelineReport(utsavId);
      case 'utilization':
        return this.establishmentService.getUtilizationReport(utsavId);
      case 'movements':
        return this.establishmentService.getMovementsReport(utsavId);
      case 'noShows':
        return this.establishmentService.getNoShowsReport(utsavId);
      case 'demandSupply':
        return this.establishmentService.getDemandSupplyReport(utsavId);
      case 'cancellations':
        return this.establishmentService.getCancellationsReport(utsavId);
      case 'velocity':
        return this.establishmentService.getBookingVelocityReport(utsavId);
      case 'guests':
        return this.establishmentService.getGuestCompositionReport(utsavId);
      case 'frontdesk':
        return this.establishmentService.getFrontdeskActivityReport(utsavId);
      default:
        return this.establishmentService.getOccupancyReport(utsavId);
    }
  }

  get activeReportLabel(): string {
    return this.reportTabs.find((t) => t.key === this.activeReport)?.label || 'Report';
  }

  toggleProperty(propertyId: number): void {
    this.expandedPropertyId = this.expandedPropertyId === propertyId ? null : propertyId;
  }

  // -- CSV export: the main table of the active tab --
  exportCsv(): void {
    if (!this.report) return;
    const r = this.report;
    const utsavName = String(r.utsavName || this.utsavs.find((u) => u.utsavId === this.utsav.value)?.name || 'utsav')
      .replace(/[^a-zA-Z0-9-]+/g, '-')
      .toLowerCase();
    const out = (name: string, columns: CsvColumn[], rows: any[]) => downloadCsv(`${utsavName}-${name}`, columns, rows || []);
    const kv = (obj: any, keyLabel: string, valueLabel: string) =>
      out(this.activeReport, [{ key: 'key', label: keyLabel }, { key: 'value', label: valueLabel }],
        Object.entries(obj || {}).map(([key, value]) => ({ key, value })));
    switch (this.activeReport) {
      case 'occupancy':
        return out('occupancy', [{ key: 'type', label: 'Capacity type' }, { key: 'target', label: 'Target' }, { key: 'assigned', label: 'Assigned' }], [
          { type: 'Individual (dormitory)', target: r.target?.individualCapacity ?? 0, assigned: r.assigned?.individual ?? 0 },
          { type: 'Family (room)', target: r.target?.familyCapacity ?? 0, assigned: r.assigned?.family ?? 0 },
          { type: 'Bulk (hall)', target: r.target?.bulkCapacity ?? 0, assigned: r.assigned?.bulk ?? 0 },
          { type: 'Total', target: r.target?.totalCapacity ?? 0, assigned: r.assigned?.total ?? 0 },
        ]);
      case 'summary':
        return kv({ ...(r.bookings || {}), guests: r.guests?.total, male: r.guests?.male, female: r.guests?.female, children: r.guests?.children }, 'Measure', 'Value');
      case 'timeline':
        return out('occupancy-timeline', [{ key: 'date', label: 'Date' }, { key: 'occupied', label: 'Occupied beds' }, { key: 'pct', label: '% of capacity' },
          { key: 'DORMITORY', label: 'Dormitory' }, { key: 'ROOM', label: 'Room' }, { key: 'HALL', label: 'Hall' }], r.days);
      case 'utilization':
        return out('utilization', [{ key: 'propertyName', label: 'Property' }, { key: 'totalRooms', label: 'Rooms' }, { key: 'activeRooms', label: 'Active rooms' },
          { key: 'roomsInUtsav', label: 'Rooms in utsav' }, { key: 'capacityInUtsav', label: 'Capacity in utsav' }, { key: 'bedsAssigned', label: 'Beds assigned' },
          { key: 'unassignedActiveRooms', label: 'Active rooms not in utsav' }, { key: 'utilizationPct', label: 'Utilization %' }], r.properties);
      case 'movements':
        return out('arrivals-departures', [{ key: 'date', label: 'Date' }, { key: 'arrivals', label: 'Arrivals (bookings)' }, { key: 'arrivalGuests', label: 'Arriving guests' },
          { key: 'arrived', label: 'Arrived' }, { key: 'departures', label: 'Departures (bookings)' }, { key: 'departureGuests', label: 'Departing guests' }, { key: 'departed', label: 'Departed' }], r.days);
      case 'noShows':
        return out('no-shows', [{ key: 'list', label: 'List' }, { key: 'bookingId', label: 'Booking ID' }, { key: 'familyCode', label: 'Family code' }, { key: 'guestCount', label: 'Guests' },
          { key: 'fromDate', label: 'From' }, { key: 'toDate', label: 'To' }, { key: 'rooms', label: 'Rooms' }],
          [...(r.overdueCheckIns || []).map((b: any) => ({ list: 'Overdue check-in', ...b })), ...(r.overdueCheckOuts || []).map((b: any) => ({ list: 'Overdue check-out', ...b }))]);
      case 'demandSupply':
        return out('demand-supply', [{ key: 'bucket', label: 'Booking size' }, { key: 'bookings', label: 'Bookings' }, { key: 'guests', label: 'Guests' },
          { key: 'unassignedBookings', label: 'Unassigned bookings' }, { key: 'beds.DORMITORY', label: 'Dormitory beds' }, { key: 'beds.ROOM', label: 'Room beds' }, { key: 'beds.HALL', label: 'Hall beds' }, { key: 'mismatchedBeds', label: 'Mismatched beds' }], r.demand);
      case 'cancellations':
        return out('cancellations', [{ key: 'bookingId', label: 'Booking ID' }, { key: 'familyCode', label: 'Family code' }, { key: 'guestCount', label: 'Guests' },
          { key: 'fromDate', label: 'From' }, { key: 'toDate', label: 'To' }, { key: 'bookingStatus', label: 'Status' }, { key: 'reason', label: 'Reason' }, { key: 'changedOn', label: 'Changed on' }, { key: 'heldBeds', label: 'Held beds' }], r.bookings);
      case 'velocity':
        return out('booking-velocity', [{ key: 'date', label: 'Date' }, { key: 'bookings', label: 'Bookings' }, { key: 'guests', label: 'Guests' }, { key: 'live', label: 'Live' },
          { key: 'cumulativeBookings', label: 'Cumulative bookings' }, { key: 'cumulativeGuests', label: 'Cumulative guests' }, { key: 'cumulativeLive', label: 'Cumulative live' }], r.days);
      case 'guests':
        return out('guest-composition', [{ key: 'label', label: 'Group' }, { key: 'guests', label: 'Guests' }, { key: 'bookings', label: 'Bookings' }], [
          ...(r.guestsByType || []).map((x: any) => ({ label: `Type: ${x.guestType}`, guests: x.guests })),
          ...(r.guestsByGender || []).map((x: any) => ({ label: `Gender: ${x.gender}`, guests: x.guests })),
          ...(r.bookingsBySize || []).map((x: any) => ({ label: `Size: ${x.size}`, guests: x.guests, bookings: x.bookings })),
        ]);
      case 'frontdesk':
        return out('frontdesk-activity', [{ key: 'at', label: 'When (IST)' }, { key: 'event', label: 'Event' }, { key: 'bookingId', label: 'Booking ID' },
          { key: 'familyCode', label: 'Family code' }, { key: 'guestCount', label: 'Guests' }, { key: 'operator', label: 'Operator' }], r.recent);
    }
  }
}
